import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/* Creator-confirmed sample receipt — shared admin types/helpers.
 * Source of truth: cr_hires.sample_receipt_* / sample_problem_*
 * (supabase/migrations/20260928000000_cr_hires_sample_receipt.sql), written
 * only by the Brand-Dashboard worker. See components/SampleReceiptCard. */

export type SampleProblemType = 'not_arrived' | 'damaged' | 'wrong_item' | 'missing_items' | 'other';

export interface SampleReceipt {
  sample_receipt_status: 'received' | 'problem' | null;
  sample_received_at: string | null;
  /** Who/what set sample_receipt_status to its current value — see
   *  20260929000004_cr_hires_sample_receipt_source.sql. 'auto_video_submission'
   *  means the creator never answered; submitting a video before this fired
   *  implied the sample had arrived. */
  sample_receipt_source: 'creator' | 'admin' | 'auto_video_submission' | null;
  sample_problem_type: SampleProblemType | null;
  sample_problem_note: string | null;
  sample_problem_reported_at: string | null;
  /** Who raised the problem — see 20261007000005. Null on rows from before it. */
  sample_problem_reported_by: 'creator' | 'admin' | null;
  sample_problem_resolved_at: string | null;
  sample_problem_resolved_by: 'creator' | 'admin' | 'auto_video_submission' | null;
  sample_problem_resolution_note: string | null;
}

export const SAMPLE_RECEIPT_COLUMNS =
  'sample_receipt_status, sample_received_at, sample_receipt_source, sample_problem_type, sample_problem_note, ' +
  'sample_problem_reported_at, sample_problem_reported_by, sample_problem_resolved_at, sample_problem_resolved_by, sample_problem_resolution_note';

export const SAMPLE_PROBLEM_LABEL: Record<SampleProblemType, string> = {
  not_arrived: "Hasn't arrived",
  damaged: 'Arrived damaged',
  wrong_item: 'Wrong item',
  missing_items: 'Items missing',
  other: 'Other issue',
};

/** Receipt per hire id. Never throws — {} on failure (e.g. migration not yet
 *  applied), so callers' main hire queries are never affected. */
export async function fetchSampleReceipts(hireIds: string[]): Promise<Record<string, SampleReceipt>> {
  const out: Record<string, SampleReceipt> = {};
  try {
    for (let i = 0; i < hireIds.length; i += 200) {
      const { data, error } = await supabase
        .from('cr_hires')
        .select(`id, ${SAMPLE_RECEIPT_COLUMNS}`)
        .in('id', hireIds.slice(i, i + 200));
      if (error) throw error;
      for (const row of (data ?? []) as unknown as (SampleReceipt & { id: string })[]) {
        const { id, ...receipt } = row;
        out[id] = receipt;
      }
    }
  } catch (e) {
    console.warn('[sample-receipt] lookup failed', e);
  }
  return out;
}

/** Every cached view of a hire's sample receipt: the hire page's card, the
 *  Creator Payments listing badges + "sample issue" filter, and the campaign
 *  detail Hires tab. Call after ANY receipt write so none of them keeps
 *  showing the old state until a manual refresh. List queries are matched by
 *  key prefix (their keys also carry the hire-id list); inactive ones are just
 *  marked stale and refetch when their page next mounts. */
export function invalidateSampleReceiptQueries(qc: QueryClient, hireId: string) {
  qc.invalidateQueries({ queryKey: ['hire-sample-receipt', hireId] });
  qc.invalidateQueries({ queryKey: ['creator-payments-sample-receipts'] });
  qc.invalidateQueries({ queryKey: ['roster-campaign-sample-receipts'] });
}
